import React from 'react';
import { useTranslation } from 'react-i18next';
import './Footer.css';

const NAV_KEYS = [
  { key: 'contact', href: '/contact' },
  { key: 'models', href: '/models' },
  { key: 'about', href: '/about' },
  { key: 'gallery', href: '/gallery' },
  { key: 'bookShowcase', href: '/book-showcase' },
];

const LEGAL_KEYS = [
  { key: 'privacy', href: '/privacy-policy' },
  { key: 'termsOfUse', href: '/terms-of-use' },
  { key: 'cookiePolicy', href: '/cookie-policy' },
];

const SOCIAL_LINKS = [
  {
    key: 'instagram',
    href: 'https://www.instagram.com/unimar.mk/',
    label: 'Instagram',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="footer-ig-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FEDA75" />
            <stop offset="25%" stopColor="#FA7E1E" />
            <stop offset="50%" stopColor="#D62976" />
            <stop offset="75%" stopColor="#962FBF" />
            <stop offset="100%" stopColor="#4F5BD5" />
          </linearGradient>
        </defs>
        <path fill="url(#footer-ig-gradient)" d="M12 0C8.74 0 8.333.014 7.053.072 5.775.131 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.014 8.333 0 8.74 0 12s.014 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.986 8.74 24 12 24s3.667-.014 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.058-1.28.072-1.687.072-4.947s-.014-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.014 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
      </svg>
    ),
  },
  {
    key: 'facebook',
    href: 'https://www.facebook.com/p/Unimar-61586908032334/',
    label: 'Facebook',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-2.413c0-4.07 2.479-6.39 6.31-6.39 1.65 0 3.06.135 3.474.196v3.65l-2.137.001c-1.654 0-2.013.792-2.013 1.96v2.997h4.078l-.62 3.667h-3.453v7.98H9.101z" />
      </svg>
    ),
  },
];

function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="footer">
      <div className="footer__inner">
        {/* Logo */}
        <a href="/" className="footer__logo">
          <img src="/logo-unimar.webp" alt="Unimar" className="footer__logo-img" />
        </a>

        {/* Navigation */}
        <nav className="footer__nav">
          {NAV_KEYS.map((link) => (
            <a key={link.key} href={link.href} className="footer__nav-link">
              {t(`footer.${link.key}`)}
            </a>
          ))}
        </nav>

        <div className="footer__divider" />

        {/* Support links */}
        <div className="footer__support">
          <p className="footer__support-link">{t('footer.support')}</p>
          <a href="tel:+38976422242" className="footer__support-link footer__phone-link">+389 76 42 22 42</a>
          <a href="mailto:office@unimar.mk" className="footer__support-link footer__email-link">office@unimar.mk</a>
          <p className="footer__support-link">{t('footer.stayConnected')}</p>
          <div className="footer__social">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.key}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="footer__social-link"
                aria-label={social.label}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Legal */}
        <div className="footer__legal">
          {LEGAL_KEYS.map((link) => (
            <a key={link.key} href={link.href} className="footer__legal-link">
              {t(`footer.${link.key}`)}
            </a>
          ))}
        </div>

        {/* Developer credit */}
        <div className="footer__dev">
          {t('footer.developedBy')}{' '}
          <a href="https://ocs.mk/" target="_blank" rel="noopener noreferrer" className="footer__dev-link">
            OCS
          </a>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
